import { useCallback, useState } from 'react';

/** The document a delete confirmation is currently open for. */
export interface PendingDelete {
  parentFolderId: string;
  documentId: string;
  fileName: string;
}

interface UseDeleteConfirmationArgs {
  /** Performs the delete; returns `true` on success, `false` on failure. */
  deleteFile: (parentFolderId: string, documentId: string) => Promise<boolean>;
}

interface UseDeleteConfirmationReturn {
  /** The document awaiting delete confirmation, or null when the modal is closed. */
  pendingDelete: PendingDelete | null;
  /** True while the confirmed delete is in flight (drives the modal's loading state). */
  isDeleting: boolean;
  /** Inline error to show in the modal after a failed delete (null when none). */
  error: string | null;
  /** Opens the confirmation for a document (from the tree's delete action). */
  requestDelete: (target: PendingDelete) => void;
  /** Runs the delete for the pending document; closes the modal on success, shows error on failure. */
  confirmDelete: () => Promise<void>;
  /** Closes the confirmation without deleting. */
  cancel: () => void;
}

/**
 * Owns the delete-confirmation lifecycle for Step 3: which document is pending, whether its
 * delete is in flight, and any failure message. On confirm it runs the injected `deleteFile`
 * executor — on success it closes the modal (the refreshed listing drops the row), on failure it
 * keeps the modal open with an inline error so the user can retry. Kept out of the presentation
 * component and local to Step 3, since nothing outside Step 3 opens a delete.
 */
export const useDeleteConfirmation = ({ deleteFile }: UseDeleteConfirmationArgs): UseDeleteConfirmationReturn => {
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestDelete = useCallback((target: PendingDelete) => {
    setError(null);
    setPendingDelete(target);
  }, []);

  // Ignore dismissal while a delete is in flight. The modal has three dismiss paths — the Cancel
  // button (disabled during the delete), the header X, and the Escape key — but the latter two are
  // built into Cloudscape's Modal and fire onDismiss regardless of the button's disabled state. If
  // an unguarded cancel cleared pendingDelete mid-flight, the modal would unmount while the in-flight
  // confirmDelete kept running, then report failure onto a closed modal (the user never learns it
  // failed). Guarding here keeps the hook self-consistent no matter how the caller wires onDismiss.
  const cancel = useCallback(() => {
    if (isDeleting) return;
    setPendingDelete(null);
    setError(null);
  }, [isDeleting]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete || isDeleting) return;
    setIsDeleting(true);
    setError(null);
    const ok = await deleteFile(pendingDelete.parentFolderId, pendingDelete.documentId);
    setIsDeleting(false);
    if (ok) {
      setPendingDelete(null);
    } else {
      setError('Delete failed. Try again.');
    }
  }, [pendingDelete, isDeleting, deleteFile]);

  return { pendingDelete, isDeleting, error, requestDelete, confirmDelete, cancel };
};
